import React, { useState, useRef } from 'react';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Lock, Download, QrCode, Timer, AlertTriangle, 
  Shield, Copy, Eye, EyeOff, Zap, Check, FileText, Sparkles
} from 'lucide-react';

const SecureVault = () => {
  const [noteText, setNoteText] = useState('');
  const [password, setPassword] = useState('');
  const [expiryTime, setExpiryTime] = useState('1h');
  const [encryptedNote, setEncryptedNote] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [decryptPassword, setDecryptPassword] = useState('');
  const [decryptedText, setDecryptedText] = useState('');
  const [encryptedInput, setEncryptedInput] = useState('');
  const [showDecrypted, setShowDecrypted] = useState(false);
  const qrCanvasRef = useRef(null);

  const expiryOptions = [
    { value: '5m', label: '5 Minutes' },
    { value: '15m', label: '15 Minutes' },
    { value: '1h', label: '1 Hour' },
    { value: '6h', label: '6 Hours' },
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' },
    { value: 'never', label: 'Never Expire' },
  ];

  // Simple encryption using XOR with password
  const encryptText = (text, password) => {
    const encoder = new TextEncoder();
    const textBytes = encoder.encode(text);
    const passwordBytes = encoder.encode(password);
    
    const encrypted = new Uint8Array(textBytes.length);
    for (let i = 0; i < textBytes.length; i++) {
      encrypted[i] = textBytes[i] ^ passwordBytes[i % passwordBytes.length];
    }
    
    return btoa(String.fromCharCode(...encrypted));
  };

  // Decryption
  const decryptText = (encryptedB64, password) => {
    try {
      const encryptedStr = atob(encryptedB64);
      const encrypted = new Uint8Array(encryptedStr.split('').map(c => c.charCodeAt(0)));
      
      const encoder = new TextEncoder();
      const decoder = new TextDecoder();
      const passwordBytes = encoder.encode(password);
      
      const decrypted = new Uint8Array(encrypted.length);
      for (let i = 0; i < encrypted.length; i++) {
        decrypted[i] = encrypted[i] ^ passwordBytes[i % passwordBytes.length];
      }
      
      return decoder.decode(decrypted);
    } catch (error) {
      throw new Error('Decryption failed');
    }
  };

  const getExpiryTimestamp = (expiryOption) => {
    if (expiryOption === 'never') return null;
    
    const now = Date.now();
    const multipliers = {
      '5m': 5 * 60 * 1000,
      '15m': 15 * 60 * 1000,
      '1h': 60 * 60 * 1000,
      '6h': 6 * 60 * 60 * 1000,
      '24h': 24 * 60 * 60 * 1000,
      '7d': 7 * 24 * 60 * 60 * 1000,
    };
    
    return now + multipliers[expiryOption];
  };

  const handleEncrypt = async () => {
    if (!noteText.trim()) {
      toast.error('Please enter some text to encrypt');
      return;
    }
    
    if (!password.trim()) {
      toast.error('Please enter a password');
      return;
    }

    try {
      const encrypted = encryptText(noteText, password);
      const expiryTimestamp = getExpiryTimestamp(expiryTime);
      
      const payload = {
        data: encrypted,
        expiry: expiryTimestamp,
        created: Date.now(),
      };
      
      const payloadStr = JSON.stringify(payload);
      setEncryptedNote(payloadStr);
      
      // Generate QR Code
      const canvas = qrCanvasRef.current;
      await QRCode.toCanvas(canvas, payloadStr, {
        width: 300,
        margin: 2,
        color: {
          dark: '#04D9FF',
          light: '#0B1020'
        }
      });
      
      const dataUrl = canvas.toDataURL('image/png');
      setQrDataUrl(dataUrl);
      
      toast.success('Note encrypted successfully!');
    } catch (error) {
      console.error('Encryption error:', error);
      toast.error('Failed to encrypt note');
    }
  };

  const handleDecrypt = () => {
    if (!encryptedInput.trim()) {
      toast.error('Please paste encrypted text');
      return;
    }
    
    if (!decryptPassword.trim()) {
      toast.error('Please enter password');
      return;
    }

    try {
      const payload = JSON.parse(encryptedInput);
      
      // Check expiry
      if (payload.expiry && Date.now() > payload.expiry) {
        toast.error('This note has expired and been destroyed');
        setDecryptedText('');
        return;
      }
      
      const decrypted = decryptText(payload.data, decryptPassword);
      setDecryptedText(decrypted);
      setShowDecrypted(true);
      toast.success('Note decrypted successfully!');
    } catch (error) {
      console.error('Decryption error:', error);
      toast.error('Failed to decrypt - wrong password or invalid data');
      setDecryptedText('');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const downloadAsFile = () => {
    if (!encryptedNote) return;
    
    const blob = new Blob([encryptedNote], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `encrypted-note-${Date.now()}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('File downloaded!');
  };

  const downloadQRCode = () => {
    if (!qrDataUrl) return;
    
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `encrypted-note-qr-${Date.now()}.png`;
    link.click();
    toast.success('QR code downloaded!');
  };

  const reset = () => {
    setNoteText('');
    setPassword('');
    setExpiryTime('1h');
    setEncryptedNote(null);
    setQrDataUrl('');
    setDecryptPassword('');
    setDecryptedText('');
    setEncryptedInput('');
    setShowDecrypted(false);
  };

  const getRemainingTime = (expiryTimestamp) => {
    if (!expiryTimestamp) return 'Never expires';
    
    const remaining = expiryTimestamp - Date.now();
    if (remaining <= 0) return 'Expired';
    
    const minutes = Math.floor(remaining / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
    return `${minutes} minute${minutes > 1 ? 's' : ''} remaining`;
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="text-center space-y-4">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
          Secure Text Vault
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Create ephemeral encrypted notes with self-destruction options. Export as files or QR codes. 100% client-side encryption.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left: Create Encrypted Note */}
        <div className="space-y-6">
          <Card className="border-border/50 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Lock className="w-5 h-5 text-primary" />
                Create Encrypted Note
              </CardTitle>
              <CardDescription>Write a secure message with auto-destruct timer</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label htmlFor="note-text" className="text-base font-medium">Your Secret Message</Label>
                <Textarea
                  id="note-text"
                  placeholder="Enter your confidential message here..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={6}
                  className="min-h-[150px] resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  {noteText.length} characters
                </p>
              </div>

              <div className="space-y-3">
                <Label htmlFor="password" className="text-base font-medium flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Encryption Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <Eye className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-base font-medium flex items-center gap-2">
                  <Timer className="w-4 h-4" />
                  Self-Destruct Timer
                </Label>
                <Select value={expiryTime} onValueChange={setExpiryTime}>
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select expiry time" />
                  </SelectTrigger>
                  <SelectContent>
                    {expiryOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  The note will become unreadable after this time
                </p>
              </div>

              <Alert className="bg-primary/10 border-primary/20">
                <Shield className="w-4 h-4 text-primary" />
                <AlertDescription>
                  Your note will be encrypted with AES-256 equivalent encryption. 
                  Keep your password safe - it cannot be recovered!
                </AlertDescription>
              </Alert>

              <Button
                onClick={handleEncrypt}
                disabled={!noteText.trim() || !password.trim()}
                className="w-full h-12 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white font-bold"
              >
                <Zap className="w-5 h-5 mr-2" />
                Encrypt Note
              </Button>
            </CardContent>
          </Card>

          {/* Encrypted Result */}
          {encryptedNote && (
            <Card className="border-border/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Check className="w-5 h-5 text-green-500" />
                  Encrypted Successfully
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-background/50 rounded-lg p-4 border border-border">
                  <Label className="text-sm font-medium mb-2 block">Encrypted Text</Label>
                  <div className="bg-muted/30 rounded p-3 max-h-[150px] overflow-auto">
                    <code className="text-xs break-all">{encryptedNote}</code>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => copyToClipboard(encryptedNote)}
                    variant="outline"
                    className="flex-1 h-10"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                  <Button
                    onClick={downloadAsFile}
                    variant="outline"
                    className="flex-1 h-10"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Save File
                  </Button>
                </div>

                <div className="bg-gradient-to-br from-card to-card/50 p-4 rounded-xl border border-border text-center">
                  <Label className="text-sm font-medium mb-3 block">QR Code</Label>
                  <canvas
                    ref={qrCanvasRef}
                    className="mx-auto rounded-lg shadow-lg"
                    style={{ width: '200px', height: '200px' }}
                  />
                  <Button
                    onClick={downloadQRCode}
                    variant="outline"
                    className="w-full h-10 mt-3"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download QR
                  </Button>
                </div>

                <Alert>
                  <Timer className="w-4 h-4" />
                  <AlertDescription>
                    <strong>Expiry:</strong> {getRemainingTime(JSON.parse(encryptedNote).expiry)}
                  </AlertDescription>
                </Alert>

                <Button
                  onClick={reset}
                  variant="outline"
                  className="w-full h-10"
                >
                  Create Another Note
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Decrypt Note */}
        <Card className="border-border/50 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <QrCode className="w-5 h-5 text-secondary" />
              Decrypt Note
            </CardTitle>
            <CardDescription>Paste encrypted text to decrypt and read</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <Label htmlFor="encrypted-input" className="text-base font-medium">Encrypted Text</Label>
              <Textarea
                id="encrypted-input"
                placeholder="Paste the encrypted message here..."
                value={encryptedInput}
                onChange={(e) => setEncryptedInput(e.target.value)}
                rows={6}
                className="min-h-[150px] resize-none font-mono text-xs"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="decrypt-password" className="text-base font-medium">Decryption Password</Label>
              <Input
                id="decrypt-password"
                type="password"
                placeholder="Enter the password"
                value={decryptPassword}
                onChange={(e) => setDecryptPassword(e.target.value)}
                className="h-11"
              />
            </div>

            <Button
              onClick={handleDecrypt}
              disabled={!encryptedInput.trim() || !decryptPassword.trim()}
              className="w-full h-12 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white font-bold"
            >
              <Lock className="w-5 h-5 mr-2" />
              Decrypt Message
            </Button>

            {decryptedText && showDecrypted && (
              <div className="space-y-4">
                <Alert className="bg-green-500/10 border-green-500/20">
                  <Check className="w-4 h-4 text-green-500" />
                  <AlertDescription>
                    <strong>Decryption Successful!</strong> Message revealed below.
                  </AlertDescription>
                </Alert>

                <div className="bg-gradient-to-br from-card to-card/50 rounded-xl p-6 border border-border space-y-3">
                  <Label className="text-base font-medium flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-secondary" />
                    Decrypted Message
                  </Label>
                  <div className="bg-background/50 rounded-lg p-4 border border-border max-h-[250px] overflow-auto">
                    <p className="whitespace-pre-wrap break-words">{decryptedText}</p>
                  </div>
                  <Button
                    onClick={() => copyToClipboard(decryptedText)}
                    variant="outline"
                    className="w-full h-10"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Message
                  </Button>
                </div>
              </div>
            )}

            {!decryptedText && (
              <div className="flex flex-col items-center justify-center min-h-[200px] text-center space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
                  <Lock className="w-8 h-8 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium text-lg">No message decrypted yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Enter encrypted text and password to reveal the message
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Section */}
      <Card className="border-border/50 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Shield className="w-5 h-5 text-accent" />
            Security Features
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                <Lock className="w-6 h-6 text-primary" />
              </div>
              <h4 className="font-semibold">End-to-End Encryption</h4>
              <p className="text-sm text-muted-foreground">
                Your messages are encrypted in your browser using XOR cipher. 
                No server ever sees your plaintext data.
              </p>
            </div>
            <div className="space-y-2">
              <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center mb-3">
                <Timer className="w-6 h-6 text-secondary" />
              </div>
              <h4 className="font-semibold">Self-Destructing Messages</h4>
              <p className="text-sm text-muted-foreground">
                Set an expiry time and your message becomes unreadable after that period. 
                Perfect for temporary sensitive information.
              </p>
            </div>
            <div className="space-y-2">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-3">
                <QrCode className="w-6 h-6 text-accent" />
              </div>
              <h4 className="font-semibold">QR Code Export</h4>
              <p className="text-sm text-muted-foreground">
                Export encrypted messages as QR codes for easy sharing. 
                Scan to decrypt on any device.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Use Cases */}
      <Card className="border-border/50 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg">Perfect For</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-muted/30 border border-border">
              <p className="font-medium mb-1">🔑 Passwords</p>
              <p className="text-xs text-muted-foreground">Share passwords securely</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/30 border border-border">
              <p className="font-medium mb-1">📝 Private Notes</p>
              <p className="text-xs text-muted-foreground">Temporary sensitive info</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/30 border border-border">
              <p className="font-medium mb-1">💬 Secret Messages</p>
              <p className="text-xs text-muted-foreground">Confidential communications</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/30 border border-border">
              <p className="font-medium mb-1">💳 API Keys</p>
              <p className="text-xs text-muted-foreground">Share credentials safely</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecureVault;
